import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Edit, Image, Play, Plus, Upload, Trash2, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { api, BACKEND_URL } from '@/api';
import { resolveMediaUrl, formatDate, formatTime } from '@/utils/helpers';
import { normalizeYouTubeUrl, normalizeStreamUrl, streamUrlWasRewritten } from '@/utils/youtube';
import { describeLocalTimezone } from '@/utils/timezone';
import { MediaPickerDialog, TeamNameAutocomplete } from './AdminShared';
import { SportIcon } from './games/SportIcon';
import { GameCsvFolderImportDialog } from './games/GameCsvFolderImportDialog';
import { AdminFixtures } from './games/AdminFixtures';
import { AdminLiveScores } from './games/AdminLiveScores';

const AdminGames = ({ teams, leagues, sports, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingGame, setEditingGame] = useState(null);
  const [formData, setFormData] = useState({
    league_id: '', home_team_id: '', away_team_id: '', scheduled_time: '', status: 'scheduled',
    home_score: '', away_score: '', thumbnail: '', is_featured: false, is_published: true, background_image_url: '', featured_description: '', featured_start: '', featured_end: '', hero_zoom: 100, hero_position_x: 50, hero_position_y: 50, score_feed_url: '', video_sources: [],
    stream_provider: '', stream_url: '', production_company_id: ''
  });
  const [newSource, setNewSource] = useState({ type: 'youtube', url: '', is_free: true, embed_url: '' });
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [productionCompanies, setProductionCompanies] = useState([]);
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [newCompanyWebsite, setNewCompanyWebsite] = useState('');

  // Paginated, searchable games listing (backend /admin/games/list)
  const [games, setGames] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState(''); // debounced
  const [leagueFilter, setLeagueFilter] = useState('');
  const [loadingGames, setLoadingGames] = useState(false);

  // Debounce the search input so we don't hammer the backend on every keystroke
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchGames = useCallback(async () => {
    setLoadingGames(true);
    try {
      const res = await api.listAdminGames({ page, per_page: perPage, search, league_id: leagueFilter });
      setGames(res.data.items || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 0);
    } catch (err) {
      console.error('Failed to load games', err);
    } finally {
      setLoadingGames(false);
    }
  }, [page, perPage, search, leagueFilter]);

  useEffect(() => { fetchGames(); }, [fetchGames]);

  // When a write happens (create/edit/delete/bulk action), re-fetch
  const refreshGames = useCallback(async () => {
    await fetchGames();
    if (onRefresh) onRefresh();
  }, [fetchGames, onRefresh]);

  // Unfeature-all + DB Audit state
  const [unfeaturing, setUnfeaturing] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditResult, setAuditResult] = useState(null); // { fixes: [], total, by_category }
  const [selectedFixIds, setSelectedFixIds] = useState(() => new Set());
  const [auditApplying, setAuditApplying] = useState(false);

  const handleUnfeatureAll = async () => {
    if (!window.confirm('Unfeature ALL currently-featured games? This clears the featured flag plus any scheduled start/end window. You can re-feature any game individually afterward.')) return;
    setUnfeaturing(true);
    try {
      const res = await api.unfeatureAllGames();
      alert(`Unfeatured ${res.data.unfeatured} game${res.data.unfeatured === 1 ? '' : 's'}.`);
      refreshGames();
    } catch (err) {
      alert(err.response?.data?.detail || err.message || 'Unfeature failed');
    } finally {
      setUnfeaturing(false);
    }
  };

  const openAudit = async () => {
    setAuditOpen(true);
    setAuditLoading(true);
    setAuditResult(null);
    setSelectedFixIds(new Set());
    try {
      const res = await api.auditGames();
      setAuditResult(res.data);
      // Pre-select every AUTO-FIXABLE fix so admins can one-click apply the recommended set.
      // Warnings (e.g. broken logo) are surfaced but not pre-selected since they need manual action.
      setSelectedFixIds(new Set((res.data.fixes || []).filter(f => f.auto_fixable !== false).map(f => f.id)));
    } catch (err) {
      console.error('Audit failed', err);
      alert(err.response?.data?.detail || err.message || 'Audit failed');
      setAuditOpen(false);
    } finally {
      setAuditLoading(false);
    }
  };

  const toggleFixId = (id) => {
    setSelectedFixIds(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const applyAudit = async () => {
    const ids = Array.from(selectedFixIds);
    if (ids.length === 0) { alert('Select at least one fix to apply.'); return; }
    if (!window.confirm(`Apply ${ids.length} fix${ids.length === 1 ? '' : 'es'} to the database? This cannot be undone.`)) return;
    setAuditApplying(true);
    try {
      const res = await api.applyAuditFixes(ids);
      alert(`Applied ${res.data.applied} of ${res.data.requested} fix(es).`);
      setAuditOpen(false);
      refreshGames();
    } catch (err) {
      alert(err.response?.data?.detail || err.message || 'Apply failed');
    } finally {
      setAuditApplying(false);
    }
  };

  const sendAuditEmail = async () => {
    if (!window.confirm('Send the audit report by email to all super-admins right now? (Same payload the weekly Sunday cron sends.)')) return;
    setAuditApplying(true);
    try {
      const res = await api.sendAuditEmailNow();
      const recips = res.data.recipients?.join(', ') || '(none)';
      alert(`Sent to ${res.data.sent}/${res.data.recipients?.length || 0} recipient(s).\n\n${recips}`);
    } catch (err) {
      alert(err.response?.data?.detail || err.message || 'Send failed');
    } finally {
      setAuditApplying(false);
    }
  };

  useEffect(() => {
    api.getProductionCompaniesAdmin()
      .then(res => setProductionCompanies(res.data || []))
      .catch(() => setProductionCompanies([]));
  }, []);

  const handleAddCompany = async () => {
    if (!newCompanyName.trim()) return;
    try {
      const res = await api.createProductionCompany({
        name: newCompanyName.trim(),
        website: newCompanyWebsite.trim() || null,
        is_active: true,
      });
      setProductionCompanies([...productionCompanies, res.data]);
      setFormData({ ...formData, production_company_id: res.data.id });
      setNewCompanyName('');
      setNewCompanyWebsite('');
      setShowAddCompany(false);
    } catch (e) {
      alert('Failed to add production company');
    }
  };

  // Helper to get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'live': return 'bg-red-500 text-white';
      case 'finished': return 'bg-gray-600 text-white';
      case 'scheduled': return 'bg-sky-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  // Handle thumbnail file upload
  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a PNG, JPG, or WebP file');
      return;
    }
    
    setUploadingThumbnail(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('alt_text', 'Match thumbnail');
      uploadFormData.append('tags', 'thumbnail,match');
      
      const res = await axios.post(`${API}/admin/media/upload`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const thumbnailUrl = resolveMediaUrl(res.data.url);
      setFormData({ ...formData, thumbnail: thumbnailUrl });
    } catch (error) {
      alert('Error uploading thumbnail');
      console.error(error);
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        home_score: formData.home_score !== '' ? parseInt(formData.home_score) : null,
        away_score: formData.away_score !== '' ? parseInt(formData.away_score) : null,
        scheduled_time: new Date(formData.scheduled_time).toISOString(),
        // Convert the optional featured-window inputs from local-time strings back to UTC ISO.
        // Empty string → null so the backend clears the window and falls back to the legacy 5-day rule.
        featured_start: formData.featured_start ? new Date(formData.featured_start).toISOString() : null,
        featured_end: formData.featured_end ? new Date(formData.featured_end).toISOString() : null,
      };
      if (editingGame) {
        await api.updateGame(editingGame.id, data);
      } else {
        await api.createGame(data);
      }
      closeForm();
      refreshGames();
    } catch (error) {
      console.error('Save game failed:', error);
      const detail = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Error saving game: ${detail}`);
    }
  };

  const resetForm = () => {
    setEditingGame(null);
    setFormData({
      league_id: '', home_team_id: '', away_team_id: '', scheduled_time: '', status: 'scheduled',
      home_score: '', away_score: '', thumbnail: '', is_featured: false, is_published: true, background_image_url: '', featured_description: '', featured_start: '', featured_end: '', hero_zoom: 100, hero_position_x: 50, hero_position_y: 50, score_feed_url: '', video_sources: [],
      stream_provider: '', stream_url: '', production_company_id: ''
    });
  };
  
  const closeForm = () => {
    setShowForm(false);
    resetForm();
  };

  // Convert a UTC ISO string (from MongoDB) into the LOCAL-time string the
  // <input type="datetime-local"> field expects (YYYY-MM-DDTHH:mm). Without
  // this, the browser would render the UTC clock-time as if it were local —
  // and on save, new Date(localStr).toISOString() would shift it by the
  // user's timezone offset (the +4h jump bug).
  const isoToDatetimeLocal = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const handleEdit = (game) => {
    setEditingGame(game);
    setFormData({
      ...game,
      scheduled_time: isoToDatetimeLocal(game.scheduled_time),
      featured_start: isoToDatetimeLocal(game.featured_start),
      featured_end: isoToDatetimeLocal(game.featured_end),
      home_score: game.home_score ?? '',
      away_score: game.away_score ?? '',
      // Legacy docs may not have this field — treat as visible.
      is_published: game.is_published !== false,
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this game?')) {
      try {
        await api.deleteGame(id);
        refreshGames();
      } catch (error) {
        const detail = error.response?.data?.detail || error.message || 'Unknown error';
        alert(`Error deleting game: ${detail}`);
        console.error('Delete game failed:', error);
      }
    }
  };

  const handleTogglePublished = async (game) => {
    // Treat missing field as visible — flips to hidden.
    const nextValue = game.is_published === false;
    try {
      await api.updateGame(game.id, { is_published: nextValue });
      refreshGames();
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Could not update visibility: ${detail}`);
      console.error('Toggle published failed:', error);
    }
  };

  const addVideoSource = () => {
    if (newSource.url) {
      // Normalise YouTube URLs at the moment of add so the saved record
      // always uses the canonical watch?v=ID form the player accepts.
      let cleanedUrl = newSource.url.trim();
      if ((newSource.type || '').toLowerCase() === 'youtube') {
        cleanedUrl = normalizeYouTubeUrl(cleanedUrl);
      }
      setFormData({
        ...formData,
        video_sources: [...formData.video_sources, { ...newSource, url: cleanedUrl }]
      });
      setNewSource({ type: 'youtube', url: '', is_free: true, embed_url: '' });
    }
  };

  const removeVideoSource = (index) => {
    setFormData({
      ...formData,
      video_sources: formData.video_sources.filter((_, i) => i !== index)
    });
  };

  const getTeamsForLeague = (leagueId) => teams.filter(t => 
    t.league_ids?.includes(leagueId) || t.league_id === leagueId
  );
  const getSportForLeague = (leagueId) => {
    const league = leagues.find(l => l.id === leagueId);
    return sports.find(s => s.id === league?.sport_id);
  };

  // ====== CSV bulk import / export / template ======
  const [csvBusy, setCsvBusy] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);

  // ====== Multi-select bulk actions ======
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const allSelected = games.length > 0 && selectedIds.size === games.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(games.map(g => g.id)));
  };
  const clearSelection = () => setSelectedIds(new Set());

  // Drop selections that no longer exist (after a refresh / delete)
  useEffect(() => {
    setSelectedIds(prev => {
      const liveIds = new Set(games.map(g => g.id));
      const next = new Set(Array.from(prev).filter(id => liveIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [games]);

  const runBulk = async (kind) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      if (kind === 'delete') {
        if (!window.confirm(`Permanently delete ${ids.length} game${ids.length === 1 ? '' : 's'}? This cannot be undone.`)) return;
        const res = await api.bulkDeleteGames(ids);
        alert(`${res.data.deleted} game(s) deleted.`);
      } else if (kind === 'feature' || kind === 'unfeature') {
        const flag = kind === 'feature';
        const res = await api.bulkSetFeaturedGames(ids, flag);
        alert(`${res.data.modified} game(s) ${flag ? 'set as featured' : 'removed from featured'}.`);
      } else if (kind === 'assign-pc') {
        if (productionCompanies.length === 0) {
          alert('No production companies available. Add one first via the game form.');
          return;
        }
        const list = productionCompanies.map((p, i) => `${i + 1}. ${p.name}`).join('\n');
        const choice = window.prompt(`Assign which production company to ${ids.length} game(s)?\nEnter the number, or leave blank to UNASSIGN:\n\n${list}`);
        if (choice === null) return; // user cancelled
        const trimmed = (choice || '').trim();
        let pcId = '';
        if (trimmed) {
          const idx = parseInt(trimmed, 10) - 1;
          if (Number.isNaN(idx) || idx < 0 || idx >= productionCompanies.length) {
            alert('Invalid choice.');
            return;
          }
          pcId = productionCompanies[idx].id;
        }
        const res = await api.bulkSetProductionCompanyGames(ids, pcId);
        alert(`${res.data.modified} game(s) ${pcId ? 'assigned to ' + productionCompanies.find(p => p.id === pcId).name : 'unassigned from any production company'}.`);
      }
      clearSelection();
      refreshGames();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Unknown error';
      alert(`Bulk action failed: ${detail}`);
      console.error(err);
    } finally {
      setBulkBusy(false);
    }
  };

  const downloadBlob = async (axiosPromise, fallbackName) => {
    const res = await axiosPromise;
    const cd = res.headers?.['content-disposition'] || '';
    const match = cd.match(/filename="?([^"]+)"?/);
    const name = match ? match[1] : fallbackName;
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadGamesTemplate = async () => {
    try { await downloadBlob(api.gamesCsvTemplate(), 'wpsltv_games_template.csv'); }
    catch (e) { alert('Template download failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const exportGamesCsv = async () => {
    setCsvBusy(true);
    try { await downloadBlob(api.exportGamesCsv(), `wpsltv_games_${new Date().toISOString().slice(0,10)}.csv`); }
    catch (e) { alert('Export failed: ' + (e.response?.data?.detail || e.message)); }
    finally { setCsvBusy(false); }
  };

  // Robust CSV parser (handles quoted commas)
  const parseCsv = (text) => {
    const lines = text.replace(/\r\n/g, '\n').replace(/^\uFEFF/, '').split('\n').filter(l => l.trim().length);
    if (lines.length < 2) return { headers: [], rows: [] };
    const splitLine = (line) => {
      const out = []; let cur = ''; let q = false;
      for (let i = 0; i < line.length; i++) {
        const c = line[i];
        if (q) {
          if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
          else if (c === '"') q = false;
          else cur += c;
        } else {
          if (c === '"') q = true;
          else if (c === ',') { out.push(cur); cur = ''; }
          else cur += c;
        }
      }
      out.push(cur);
      return out;
    };
    const headers = splitLine(lines[0]).map(h => h.trim().toLowerCase());
    const rows = lines.slice(1).map(l => {
      const cells = splitLine(l);
      const obj = {};
      headers.forEach((h, i) => { if (cells[i] !== undefined && cells[i] !== '') obj[h] = cells[i].trim(); });
      return obj;
    });
    return { headers, rows };
  };

  const handleImportResult = (data) => {
    const { created, updated, skipped = 0, errors = [], total, _uploadedImages = 0, _createdTeams = [] } = data;
    let msg = `${created} created, ${updated} updated, ${skipped} skipped of ${total} rows`;
    if (_createdTeams.length > 0) {
      msg += `\nCreated ${_createdTeams.length} new team${_createdTeams.length === 1 ? '' : 's'}: ${_createdTeams.join(', ')}`;
    }
    if (_uploadedImages > 0) {
      msg += `\n${_uploadedImages} image file${_uploadedImages === 1 ? '' : 's'} uploaded to Media Library.`;
    }
    if (skipped > 0) {
      msg += `\n\nTip: ${skipped} row(s) matched existing games and were skipped. Re-upload with "Update existing games when matched by team + time" enabled to overwrite them.`;
    }
    if (errors.length) {
      msg += `\n\n${errors.length} error(s):\n` + errors.slice(0, 8).map(e => `  Row ${e.row}: ${e.error}`).join('\n');
    }
    alert(msg);
    refreshGames();
  };

  return (
    <div className="space-y-6">
      <GameCsvFolderImportDialog
        open={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        onResult={handleImportResult}
        parseCsv={parseCsv}
        teams={teams}
        leagues={leagues}
      />
      <div className="flex flex-wrap justify-between items-center gap-2">
        <h2 className="text-2xl font-bold text-white">
          Games <span className="text-gray-400 text-base font-normal">({total})</span>
        </h2>
        <div className="flex flex-wrap gap-2 items-center">
          <Button
            onClick={downloadGamesTemplate}
            variant="outline"
            className="border-wpsl-blue/50 text-wpsl-blue hover:bg-wpsl-blue/10"
            data-testid="games-csv-template"
          >
            CSV Template
          </Button>
          <Button
            onClick={() => setShowImportDialog(true)}
            variant="outline"
            disabled={csvBusy}
            className="border-wpsl-blue bg-wpsl-blue/10 text-wpsl-blue hover:bg-wpsl-blue hover:text-white"
            data-testid="games-csv-import"
          >
            <Upload className="w-4 h-4 mr-2" />
            {csvBusy ? 'Importing…' : 'Bulk Import CSV'}
          </Button>
          <Button
            onClick={exportGamesCsv}
            variant="outline"
            disabled={csvBusy || games.length === 0}
            className="border-wpsl-blue/50 text-wpsl-blue hover:bg-wpsl-blue/10"
            data-testid="games-csv-export"
          >
            {csvBusy ? 'Exporting…' : 'Export Games CSV'}
          </Button>
          <Button
            onClick={handleUnfeatureAll}
            variant="outline"
            disabled={unfeaturing}
            className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10"
            data-testid="games-unfeature-all"
            title="Clear the featured flag from every game in the database."
          >
            {unfeaturing ? 'Unfeaturing…' : 'Unfeature All'}
          </Button>
          <Button
            onClick={openAudit}
            variant="outline"
            className="border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10"
            data-testid="games-audit-btn"
            title="Inspect every game's stream URLs for known fixable issues — review proposed changes before applying."
          >
            Audit & Fix
          </Button>
          <Button onClick={() => { setShowForm(true); resetForm(); }} className="bg-sky-300 hover:bg-sky-400" data-testid="add-game-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Game
          </Button>
        </div>
      </div>

      {/* Search + filters + pagination controls */}
      <div className="flex flex-wrap gap-2 items-center bg-gray-800/40 border border-gray-700/50 rounded-lg p-3">
        <div className="flex-1 min-w-[220px]">
          <Input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by team or conference name…"
            className="bg-gray-700 border-gray-600 text-white"
            data-testid="games-search-input"
          />
        </div>
        <Select value={leagueFilter || 'all'} onValueChange={(v) => { setLeagueFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="bg-gray-700 border-gray-600 text-white w-[220px]" data-testid="games-league-filter">
            <SelectValue placeholder="All conferences" />
          </SelectTrigger>
          <SelectContent className="bg-gray-800 border-gray-700">
            <SelectItem value="all" className="text-white">All conferences</SelectItem>
            {leagues.map(l => (
              <SelectItem key={l.id} value={l.id} className="text-white">{l.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={String(perPage)} onValueChange={(v) => { setPerPage(parseInt(v, 10)); setPage(1); }}>
          <SelectTrigger className="bg-gray-700 border-gray-600 text-white w-[110px]" data-testid="games-per-page">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-gray-800 border-gray-700">
            {[25, 50, 100, 200].map(n => (
              <SelectItem key={n} value={String(n)} className="text-white">{n} / page</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1 ml-auto">
          <Button
            size="sm"
            variant="outline"
            className="border-gray-600 text-gray-300"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1 || loadingGames}
            data-testid="games-prev-page"
          >← Prev</Button>
          <span className="text-xs text-gray-300 px-2 min-w-[80px] text-center" data-testid="games-page-indicator">
            Page {page} of {Math.max(1, pages)}
          </span>
          <Button
            size="sm"
            variant="outline"
            className="border-gray-600 text-gray-300"
            onClick={() => setPage(p => Math.min(pages || 1, p + 1))}
            disabled={page >= pages || loadingGames}
            data-testid="games-next-page"
          >Next →</Button>
        </div>
      </div>

      {showForm && (
        <Card className="bg-gray-800/50 border-sky-900/30">
          <CardHeader>
            <CardTitle className="text-white">{editingGame ? 'Edit Game' : 'Add New Game'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Conference</Label>
                  <Select value={formData.league_id} onValueChange={(v) => setFormData({ ...formData, league_id: v, home_team_id: '', away_team_id: '' })}>
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                      <SelectValue placeholder="Select Conference" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      {leagues.map(league => {
                        const sport = getSportForLeague(league.id);
                        return (
                          <SelectItem key={league.id} value={league.id} className="text-white hover:bg-gray-700">
                            <span className="flex items-center gap-1.5"><SportIcon sport={sport} className="w-3.5 h-3.5 inline" /> {league.name}</span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Scheduled Time</Label>
                  <Input 
                    type="datetime-local"
                    value={formData.scheduled_time}
                    onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white"
                    required
                  />
                  <p className="text-xs text-gray-400 mt-1" data-testid="game-time-tz-hint">
                    Your timezone: <span className="text-sky-300">{describeLocalTimezone()}</span>
                  </p>
                </div>
                <div>
                  <Label className="text-gray-300">Home Team</Label>
                  {formData.league_id && getTeamsForLeague(formData.league_id).length === 0 ? (
                    <div className="flex items-center gap-2 p-2 bg-amber-900/20 border border-amber-700/30 rounded-md text-amber-300 text-sm">
                      <AlertCircle className="w-4 h-4 shrink-0" /> No teams assigned to this league. Add teams in the Teams tab first.
                    </div>
                  ) : (
                    <Select value={formData.home_team_id} onValueChange={(v) => setFormData({ ...formData, home_team_id: v })} disabled={!formData.league_id}>
                      <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                        <SelectValue placeholder="Select Home Team" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700">
                        {getTeamsForLeague(formData.league_id).map(team => (
                          <SelectItem key={team.id} value={team.id} className="text-white hover:bg-gray-700">
                            {team.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div>
                  <Label className="text-gray-300">Away Team</Label>
                  {formData.league_id && getTeamsForLeague(formData.league_id).length === 0 ? (
                    <div className="flex items-center gap-2 p-2 bg-amber-900/20 border border-amber-700/30 rounded-md text-amber-300 text-sm">
                      <AlertCircle className="w-4 h-4 shrink-0" /> No teams assigned to this league.
                    </div>
                  ) : (
                    <Select value={formData.away_team_id} onValueChange={(v) => setFormData({ ...formData, away_team_id: v })} disabled={!formData.league_id}>
                      <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                        <SelectValue placeholder="Select Away Team" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700">
                        {getTeamsForLeague(formData.league_id).map(team => (
                          <SelectItem key={team.id} value={team.id} className="text-white hover:bg-gray-700">
                            {team.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div>
                  <Label className="text-gray-300">Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      <SelectItem value="scheduled" className="text-white hover:bg-gray-700">Scheduled</SelectItem>
                      <SelectItem value="live" className="text-white hover:bg-gray-700">Live</SelectItem>
                      <SelectItem value="finished" className="text-white hover:bg-gray-700">Finished</SelectItem>
                      <SelectItem value="postponed" className="text-white hover:bg-gray-700">Postponed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-gray-300">Match Thumbnail</Label>
                  <div className="flex gap-2">
                    <Input 
                      value={formData.thumbnail}
                      onChange={(e) => setFormData({ ...formData, thumbnail: e.target.value })}
                      className="bg-gray-700 border-gray-600 text-white flex-1"
                      placeholder="URL or upload below"
                    />
                  </div>
                  <div className="flex items-center gap-3 mt-2">
                    <label className="cursor-pointer">
                      <input 
                        type="file" 
                        accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
                        onChange={handleThumbnailUpload}
                        className="hidden"
                      />
                      <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded text-sm ${uploadingThumbnail ? 'bg-gray-600 text-gray-400' : 'bg-sky-600 hover:bg-sky-700 text-white'}`}>
                        <Upload className="w-4 h-4" /> {uploadingThumbnail ? 'Uploading...' : 'Upload Thumbnail'}
                      </span>
                    </label>
                    {formData.thumbnail && (
                      <img 
                        src={resolveMediaUrl(formData.thumbnail)} 
                        alt="Thumbnail preview" 
                        className="h-16 w-24 object-cover rounded"
                      />
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-gray-300">Home Score</Label>
                  <Input 
                    type="number"
                    value={formData.home_score}
                    onChange={(e) => setFormData({ ...formData, home_score: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="-"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Away Score</Label>
                  <Input 
                    type="number"
                    value={formData.away_score}
                    onChange={(e) => setFormData({ ...formData, away_score: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="-"
                  />
                </div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_published !== false}
                  onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                  className="rounded"
                  data-testid="is-published-checkbox"
                />
                <span className="text-gray-300 text-sm">
                  Publish on public site
                  <span className="text-xs text-gray-500 ml-2">(uncheck to hide from fans — admin only)</span>
                </span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={formData.is_featured}
                  onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                  className="rounded"
                  data-testid="is-featured-checkbox"
                />
                <span className="text-gray-300 text-sm">Featured Game (shows in hero carousel)</span>
              </label>

              {formData.is_featured && (
                <div className="space-y-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <div className="flex items-center gap-2 text-amber-400 text-sm font-medium">
                    <Image className="w-4 h-4" />
                    Hero Image Required for Featured Games
                  </div>
                  <p className="text-gray-400 text-xs">
                    Upload a high-quality, wide cinematic image (recommended: 1920x1080) for the homepage hero carousel. Without a hero image, a gradient background will be used.
                  </p>
                  <div>
                    <Label className="text-gray-300">Hero Background Image</Label>
                    <div className="flex gap-2 mt-1">
                      <Input 
                        value={formData.background_image_url || ''}
                        onChange={(e) => setFormData({ ...formData, background_image_url: e.target.value })}
                        className="bg-gray-700 border-gray-600 text-white flex-1"
                        placeholder="Background image URL for hero carousel"
                      />
                      <Button 
                        type="button" 
                        variant="outline" 
                        className="border-amber-500 text-amber-400 hover:bg-amber-500/20"
                        onClick={() => setShowMediaPicker(true)}
                      >
                        <Image className="w-4 h-4 mr-2" /> Browse
                      </Button>
                    </div>
                    {formData.background_image_url && (
                      <div className="mt-2 relative">
                        <div className="overflow-hidden rounded max-w-md h-32 bg-gray-900">
                          <img 
                            src={resolveMediaUrl(formData.background_image_url)} 
                            alt="Preview" 
                            className="w-full h-full object-cover"
                            style={{
                              transform: `scale(${(formData.hero_zoom || 100) / 100})`,
                              transformOrigin: `${formData.hero_position_x || 50}% ${formData.hero_position_y || 50}%`,
                              objectPosition: `${formData.hero_position_x || 50}% ${formData.hero_position_y || 50}%`
                            }}
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Zoom & Position Controls */}
                  {formData.background_image_url && (
                    <div className="pt-3 border-t border-amber-500/20">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-gray-300">Image Zoom & Position</Label>
                        <Button 
                          type="button" 
                          size="sm" 
                          variant="ghost" 
                          className="text-gray-400 hover:text-white text-xs"
                          onClick={() => setFormData({ ...formData, hero_zoom: 100, hero_position_x: 50, hero_position_y: 50 })}
                        >
                          Reset to Default
                        </Button>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label className="text-gray-400 text-xs mb-1 block">Zoom: {formData.hero_zoom || 100}%</Label>
                          <input
                            type="range"
                            min="100"
                            max="200"
                            step="5"
                            value={formData.hero_zoom || 100}
                            onChange={(e) => setFormData({ ...formData, hero_zoom: parseInt(e.target.value) })}
                            className="w-full accent-amber-500"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-400 text-xs mb-1 block">Horizontal: {formData.hero_position_x || 50}%</Label>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            step="5"
                            value={formData.hero_position_x || 50}
                            onChange={(e) => setFormData({ ...formData, hero_position_x: parseInt(e.target.value) })}
                            className="w-full accent-amber-500"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-400 text-xs mb-1 block">Vertical: {formData.hero_position_y || 50}%</Label>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            step="5"
                            value={formData.hero_position_y || 50}
                            onChange={(e) => setFormData({ ...formData, hero_position_y: parseInt(e.target.value) })}
                            className="w-full accent-amber-500"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Featured Match Write-up */}
                  <div className="pt-3 border-t border-amber-500/20">
                    <Label className="text-gray-300">Match Description (Info Overlay)</Label>
                    <p className="text-gray-500 text-xs mb-2">
                      Write a brief description about the match. This appears when viewers click "More Info" - similar to cable TV program info.
                    </p>
                    <textarea
                      value={formData.featured_description || ''}
                      onChange={(e) => setFormData({ ...formData, featured_description: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-3 min-h-[100px] text-sm"
                      placeholder="e.g., The defending champions face their cross-town rivals in this highly anticipated clash. Both teams are undefeated this season, making this a must-watch matchup that could determine playoff positioning..."
                    />
                    <p className="text-gray-500 text-xs mt-1">{(formData.featured_description || '').length}/500 characters</p>
                  </div>

                  {/* Featured window: optional start/end dates that gate when the game appears in the hero carousel */}
                  <div className="pt-3 border-t border-amber-500/20">
                    <Label className="text-gray-300">Featured window <span className="text-gray-500 text-xs">(optional)</span></Label>
                    <p className="text-gray-500 text-xs mb-2">
                      Only show this game in the hero carousel between these two dates. Leave blank to use the default (featured until 5 days after the game).
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-gray-400 block mb-1">Start</label>
                        <Input
                          type="datetime-local"
                          value={formData.featured_start || ''}
                          onChange={(e) => setFormData({ ...formData, featured_start: e.target.value })}
                          className="bg-gray-700 border-gray-600 text-white"
                          data-testid="featured-start-input"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-400 block mb-1">End</label>
                        <Input
                          type="datetime-local"
                          value={formData.featured_end || ''}
                          onChange={(e) => setFormData({ ...formData, featured_end: e.target.value })}
                          className="bg-gray-700 border-gray-600 text-white"
                          data-testid="featured-end-input"
                        />
                      </div>
                    </div>
                    {(formData.featured_start || formData.featured_end) && (
                      <p className="text-emerald-300 text-xs mt-2" data-testid="featured-window-tz-hint">
                        Times interpreted in your local timezone ({describeLocalTimezone()})
                      </p>
                    )}
                  </div>
                </div>
              )}

              <MediaPickerDialog
                open={showMediaPicker}
                onOpenChange={setShowMediaPicker}
                onSelect={(url) => setFormData({ ...formData, background_image_url: url })}
              />

              {/* Score Feed URL for live updates */}
              <div>
                <Label className="text-gray-300">Live Score Feed URL</Label>
                <Input 
                  value={formData.score_feed_url || ''}
                  onChange={(e) => setFormData({ ...formData, score_feed_url: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                  placeholder="https://scoreboard-live-35.preview.emergentagent.com/api/feed/text/..."
                />
                <p className="text-gray-500 text-xs mt-1">URL to fetch live score/minute text during the game</p>
              </div>

              {/* Stream Provider — WPSL TV Phase 3 */}
              <div className="border border-gray-700 rounded-lg p-4 space-y-4">
                <div>
                  <Label className="text-gray-200 text-lg block mb-1">Stream Provider</Label>
                  <p className="text-xs text-gray-500 mb-3">
                    YouTube, Facebook, Twitch → embedded movie-theater player.
                    Hudl.TV, Uscreen, VEO Live, Other → external link redirect.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <Select
                      value={formData.stream_provider || 'none'}
                      onValueChange={(v) => setFormData({ ...formData, stream_provider: v === 'none' ? '' : v })}
                    >
                      <SelectTrigger className="bg-gray-700 border-gray-600 text-white" data-testid="stream-provider-select">
                        <SelectValue placeholder="Select provider..." />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700">
                        <SelectItem value="none" className="text-white hover:bg-gray-700">— None —</SelectItem>
                        <SelectItem value="youtube" className="text-white hover:bg-gray-700">YouTube (embedded)</SelectItem>
                        <SelectItem value="facebook" className="text-white hover:bg-gray-700">Facebook Live (embedded)</SelectItem>
                        <SelectItem value="twitch" className="text-white hover:bg-gray-700">Twitch (embedded)</SelectItem>
                        <SelectItem value="hudl" className="text-white hover:bg-gray-700">Hudl.TV (external)</SelectItem>
                        <SelectItem value="uscreen" className="text-white hover:bg-gray-700">Uscreen (external)</SelectItem>
                        <SelectItem value="veo" className="text-white hover:bg-gray-700">VEO Live (external)</SelectItem>
                        <SelectItem value="nationalsoccernetwork" className="text-white hover:bg-gray-700">NationalSoccerNetwork.tv (external)</SelectItem>
                        <SelectItem value="other" className="text-white hover:bg-gray-700">Other (external)</SelectItem>
                      </SelectContent>
                    </Select>
                    <div>
                      <Input
                        value={formData.stream_url || ''}
                        onChange={(e) => setFormData({ ...formData, stream_url: e.target.value })}
                        onBlur={(e) => {
                          // Auto-clean on blur so admins don't have to think about it.
                          // Works for YouTube/Facebook/Twitch — other providers pass through.
                          const cleaned = normalizeStreamUrl(e.target.value, formData.stream_provider);
                          if (cleaned !== e.target.value) {
                            setFormData({ ...formData, stream_url: cleaned });
                          }
                        }}
                        className="bg-gray-700 border-gray-600 text-white"
                        placeholder="Stream URL"
                        data-testid="stream-url-input"
                      />
                      {(() => {
                        const p = (formData.stream_provider || '').toLowerCase();
                        if (!formData.stream_url || !['youtube', 'facebook', 'twitch'].includes(p)) return null;
                        const cleaned = normalizeStreamUrl(formData.stream_url, p);
                        if (!streamUrlWasRewritten(formData.stream_url, cleaned)) return null;
                        return (
                          <p className="text-xs text-emerald-300 mt-1" data-testid="stream-url-yt-hint">
                            Will be saved as: <code className="bg-gray-900 px-1 rounded">{cleaned}</code>
                          </p>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {/* Production Company */}
                <div>
                  <Label className="text-gray-200 block mb-1">Production Company</Label>
                  <p className="text-xs text-gray-500 mb-2">Credit attribution shown on the game page.</p>
                  <div className="flex items-center gap-2">
                    <Select
                      value={formData.production_company_id || 'none'}
                      onValueChange={(v) => setFormData({ ...formData, production_company_id: v === 'none' ? '' : v })}
                    >
                      <SelectTrigger className="bg-gray-700 border-gray-600 text-white flex-1" data-testid="production-company-select">
                        <SelectValue placeholder="Select company..." />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700 max-h-72">
                        <SelectItem value="none" className="text-white hover:bg-gray-700">— None —</SelectItem>
                        {productionCompanies.map(pc => (
                          <SelectItem key={pc.id} value={pc.id} className="text-white hover:bg-gray-700">
                            {pc.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => setShowAddCompany(!showAddCompany)}
                      className="bg-wpsl-blue hover:bg-wpsl-red text-white"
                      data-testid="add-production-company-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add new
                    </Button>
                  </div>
                  {showAddCompany && (
                    <div className="mt-3 p-3 bg-gray-900/50 rounded border border-gray-700 space-y-2">
                      <Input
                        value={newCompanyName}
                        onChange={(e) => setNewCompanyName(e.target.value)}
                        className="bg-gray-700 border-gray-600 text-white"
                        placeholder="Company name *"
                        data-testid="new-company-name"
                      />
                      <Input
                        value={newCompanyWebsite}
                        onChange={(e) => setNewCompanyWebsite(e.target.value)}
                        className="bg-gray-700 border-gray-600 text-white"
                        placeholder="Website (optional)"
                      />
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          onClick={handleAddCompany}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid="save-production-company"
                        >
                          Save Company
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => { setShowAddCompany(false); setNewCompanyName(''); setNewCompanyWebsite(''); }}
                          className="border-gray-600 text-gray-300"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Legacy Video Sources (kept for backwards compatibility — used by graphic generators) */}
              <details className="border border-gray-700 rounded-lg p-4">
                <summary className="text-gray-400 text-sm cursor-pointer">Legacy video sources (deprecated)</summary>
                <div className="mt-3">
                {formData.video_sources.map((source, index) => (
                  <div key={`${source.type}-${source.url}-${index}`} className="flex items-center gap-2 mb-2 p-2 bg-gray-700/50 rounded">
                    <Badge className={source.is_free ? 'bg-green-500' : 'bg-sky-300'}>
                      {source.type.toUpperCase()} - {source.is_free ? 'FREE' : 'PLUS'}
                    </Badge>
                    <span className="text-gray-300 flex-1 truncate">{source.url}</span>
                    <Button size="sm" variant="destructive" onClick={() => removeVideoSource(index)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
                <div className="grid grid-cols-4 gap-2 mt-4">
                  <Select value={newSource.type} onValueChange={(v) => setNewSource({ ...newSource, type: v })}>
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      <SelectItem value="youtube" className="text-white hover:bg-gray-700">YouTube</SelectItem>
                      <SelectItem value="uscreen" className="text-white hover:bg-gray-700">Plus</SelectItem>
                      <SelectItem value="nbc_sports" className="text-white hover:bg-gray-700">NBC Sports</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="col-span-2">
                    <Input
                      value={newSource.url}
                      onChange={(e) => setNewSource({ ...newSource, url: e.target.value })}
                      className="bg-gray-700 border-gray-600 text-white"
                      placeholder="Video URL"
                      data-testid="video-source-url-input"
                    />
                    {(newSource.type || '').toLowerCase() === 'youtube' && newSource.url && streamUrlWasRewritten(newSource.url, normalizeYouTubeUrl(newSource.url)) && (
                      <p className="text-xs text-emerald-300 mt-1" data-testid="youtube-url-hint">
                        Will be saved as: <code className="bg-gray-900 px-1 rounded">{normalizeYouTubeUrl(newSource.url)}</code>
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      checked={newSource.is_free}
                      onChange={(e) => setNewSource({ ...newSource, is_free: e.target.checked })}
                    />
                    <span className="text-gray-300 text-sm">Free</span>
                    <Button type="button" size="sm" onClick={addVideoSource} className="bg-green-600 hover:bg-green-700">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                </div>
              </details>

              <div className="flex gap-2">
                <Button type="submit" className="bg-sky-300 hover:bg-sky-400">Save Game</Button>
                <Button type="button" variant="outline" className="border-gray-600" onClick={closeForm}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        {/* Multi-select header (Select All + bulk-action toolbar) */}
        {games.length > 0 && (
          <div
            className={`sticky top-0 z-10 -mx-2 px-3 py-2 rounded-lg border flex flex-wrap items-center justify-between gap-3 transition-colors ${
              selectedIds.size > 0
                ? 'bg-wpsl-blue/20 border-wpsl-blue/60'
                : 'bg-gray-800/60 border-gray-700'
            }`}
            data-testid="games-bulk-toolbar"
          >
            <label className="flex items-center gap-2 text-white text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={allSelected}
                ref={el => { if (el) el.indeterminate = someSelected; }}
                onChange={toggleAll}
                className="w-4 h-4 accent-wpsl-blue"
                data-testid="games-select-all"
              />
              <span className="font-medium">
                {selectedIds.size === 0
                  ? `Select all ${games.length} game${games.length === 1 ? '' : 's'}`
                  : `${selectedIds.size} of ${games.length} selected`}
              </span>
            </label>
            {selectedIds.size > 0 && (
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10"
                  disabled={bulkBusy}
                  onClick={() => runBulk('feature')}
                  data-testid="games-bulk-feature"
                  title="Mark selected games as Featured (hero carousel)"
                >
                  ★ Feature
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-gray-500/50 text-gray-300 hover:bg-gray-500/10"
                  disabled={bulkBusy}
                  onClick={() => runBulk('unfeature')}
                  data-testid="games-bulk-unfeature"
                  title="Remove Featured flag from selected games"
                >
                  Unfeature
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-wpsl-blue/50 text-wpsl-blue hover:bg-wpsl-blue/10"
                  disabled={bulkBusy}
                  onClick={() => runBulk('assign-pc')}
                  data-testid="games-bulk-assign-pc"
                  title="Assign a Production Company to all selected games"
                >
                  Assign Production Co.
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={bulkBusy}
                  onClick={() => runBulk('delete')}
                  data-testid="games-bulk-delete"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Delete {selectedIds.size}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-gray-300 hover:bg-gray-700"
                  onClick={clearSelection}
                  data-testid="games-bulk-clear"
                >
                  Clear
                </Button>
              </div>
            )}
          </div>
        )}

        {games.map(game => {
          const checked = selectedIds.has(game.id);
          return (
          <Card key={game.id} className={`bg-gray-800/50 border ${checked ? 'border-wpsl-blue/70 ring-1 ring-wpsl-blue/40' : 'border-sky-900/30'}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleOne(game.id)}
                    className="w-4 h-4 accent-wpsl-blue"
                    data-testid={`games-row-checkbox-${game.id}`}
                    aria-label={`Select ${game.home_team_name || 'game'} vs ${game.away_team_name || ''}`}
                  />
                  <Badge className={getStatusColor(game.status)}>{game.status.toUpperCase()}</Badge>
                  {game.is_published === false && (
                    <Badge className="bg-gray-700 text-gray-200 border border-gray-500" data-testid={`game-hidden-badge-${game.id}`}>
                      HIDDEN
                    </Badge>
                  )}
                  <div>
                    <h3 className="text-white font-semibold">
                      {game.home_team_name || 'TBD'} vs {game.away_team_name || 'TBD'}
                    </h3>
                    <p className="text-sm text-sky-200">{game.sport_name} / {game.league_name}</p>
                    <p className="text-sm text-gray-400">{formatDate(game.scheduled_time)} at {formatTime(game.scheduled_time)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {game.video_sources?.length > 0 && (
                    <div className="flex gap-1">
                      {game.video_sources.map((vs, i) => (
                        <Badge key={`${vs.type}-${i}`} variant="outline" className="border-sky-400 text-sky-100">
                          {vs.type}
                        </Badge>
                      ))}
                    </div>
                  )}
                  {(game.home_score !== null || game.away_score !== null) && (
                    <div className="text-2xl font-bold text-white">
                      {game.home_score ?? '-'} - {game.away_score ?? '-'}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className={game.is_published === false ? 'border-gray-500 text-gray-400 hover:text-white' : 'border-gray-600'}
                      onClick={() => handleTogglePublished(game)}
                      title={game.is_published === false ? 'Hidden — click to show publicly' : 'Visible — click to hide from fans'}
                      data-testid={`game-toggle-published-${game.id}`}
                    >
                      {game.is_published === false ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button size="sm" variant="outline" className="border-gray-600" onClick={() => handleEdit(game)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(game.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          );
        })}
        {games.length === 0 && (
          <div className="text-center py-8 text-gray-400" data-testid="games-empty-state">
            {loadingGames
              ? 'Loading games…'
              : (search || leagueFilter)
                ? 'No games match your search. Try a different keyword or clear the filter.'
                : 'No games yet. Add teams first, then add games!'}
          </div>
        )}
      </div>

      {/* Audit & Fix dialog */}
      <Dialog open={auditOpen} onOpenChange={(v) => { if (!auditApplying) setAuditOpen(v); }}>
        <DialogContent className="max-w-3xl bg-gray-900 border-emerald-500/40">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Audit & Fix — Games &amp; Teams
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Inspects every game for stream-URL drift (YouTube / Facebook / Twitch normalisation, NationalSoccerNetwork.tv mis-tagging) and every team for missing slugs &amp; broken logo URLs.
              <br/><span className="text-amber-300">Yellow rows are warnings</span> — admin needs to fix manually, can't be auto-applied.
            </DialogDescription>
          </DialogHeader>
          {auditLoading ? (
            <div className="py-12 text-center text-gray-400" data-testid="audit-loading">Scanning database…</div>
          ) : auditResult && auditResult.total === 0 ? (
            <div className="py-12 text-center text-emerald-300" data-testid="audit-empty">
              ✓ All clear — no fixable issues found.
            </div>
          ) : auditResult ? (
            <>
              <div className="text-sm text-gray-300 mb-2" data-testid="audit-summary">
                <strong className="text-white">{auditResult.total}</strong> proposed fix{auditResult.total === 1 ? '' : 'es'}
                {Object.keys(auditResult.by_category).length > 0 && (
                  <span className="text-gray-400"> ({Object.entries(auditResult.by_category).map(([k, v]) => `${v} ${k}`).join(', ')})</span>
                )}
              </div>
              <div className="max-h-[55vh] overflow-y-auto space-y-2 pr-1">
                {auditResult.fixes.map(f => {
                  const isWarning = f.auto_fixable === false;
                  return (
                    <label
                      key={f.id}
                      className={`flex items-start gap-3 border rounded-lg p-3 ${isWarning ? 'bg-amber-900/20 border-amber-600/40 cursor-default' : 'bg-gray-800/60 hover:bg-gray-800 border-gray-700/50 cursor-pointer'}`}
                      data-testid={`audit-fix-${f.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={!isWarning && selectedFixIds.has(f.id)}
                        disabled={isWarning}
                        onChange={() => !isWarning && toggleFixId(f.id)}
                        className="mt-1 h-4 w-4 accent-emerald-500 disabled:opacity-50"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="text-[10px] uppercase tracking-wider text-gray-500 font-mono">{f.entity_type || 'game'}</span>
                          <span className="text-white text-sm font-medium">{f.game_label}</span>
                          <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${isWarning ? 'text-amber-300 bg-amber-500/15' : 'text-emerald-300 bg-emerald-500/10'}`}>
                            {f.category}
                          </span>
                          {isWarning && (
                            <span className="text-[10px] uppercase tracking-wide text-amber-300 bg-amber-500/20 px-1.5 py-0.5 rounded">
                              warning — manual fix
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-400 mb-1">{f.reason}</div>
                        <div className="text-xs font-mono">
                          <div className="text-red-300 truncate"><span className="text-gray-500">{f.field}:</span> {f.old_value || '(empty)'}</div>
                          {!isWarning && (
                            <div className="text-emerald-300 truncate">→ {f.new_value || '(empty)'}</div>
                          )}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </>
          ) : null}
          <div className="flex flex-wrap justify-between items-center gap-2 pt-3 border-t border-gray-700">
            <p className="text-xs text-gray-500">
              Heads-up: this report runs automatically every <strong className="text-gray-300">Sunday at 14:00 UTC</strong> and emails super-admins if any fixes are pending.
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="border-sky-500/50 text-sky-300 hover:bg-sky-500/10"
                onClick={sendAuditEmail}
                disabled={auditApplying}
                data-testid="audit-send-email"
                title="Send the audit-report email to all super-admins immediately. Same payload the weekly cron will send."
              >
                Email me this now
              </Button>
              <Button
                variant="outline"
                className="border-gray-600 text-gray-300"
                onClick={() => setAuditOpen(false)}
                disabled={auditApplying}
                data-testid="audit-cancel"
              >
                Close
              </Button>
              {auditResult && auditResult.total > 0 && (
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={applyAudit}
                  disabled={auditApplying || selectedFixIds.size === 0}
                  data-testid="audit-apply"
                >
                  {auditApplying ? 'Applying…' : `Apply ${selectedFixIds.size} fix${selectedFixIds.size === 1 ? '' : 'es'}`}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Admin Fixtures

export { AdminGames, AdminFixtures, AdminLiveScores };
